/**
 * Persistable One AI panel size/position (viewport coordinates).
 */

export type OneAiPanelGeometry = {
  /** Distance from left viewport edge (px). */
  left: number;
  /** Distance from top viewport edge (px). */
  top: number;
  width: number;
  height: number;
};

export const ONE_AI_PANEL_STORAGE_KEY = "atw-one-ai-panel-geometry-v1";

export const ONE_AI_PANEL_MIN_W = 300;
export const ONE_AI_PANEL_MIN_H = 380;
export const ONE_AI_PANEL_DEFAULT_W = 360;
export const ONE_AI_PANEL_DEFAULT_H = 520;

export function defaultOneAiPanelGeometry(): OneAiPanelGeometry {
  if (typeof window === "undefined") {
    return { left: 24, top: 80, width: ONE_AI_PANEL_DEFAULT_W, height: ONE_AI_PANEL_DEFAULT_H };
  }
  const width = Math.min(ONE_AI_PANEL_DEFAULT_W, window.innerWidth - 24);
  const height = Math.min(ONE_AI_PANEL_DEFAULT_H, window.innerHeight - 48);
  return {
    width,
    height,
    left: Math.max(12, window.innerWidth - width - 16),
    top: Math.max(12, window.innerHeight - height - 56),
  };
}

export function clampOneAiPanelGeometry(g: OneAiPanelGeometry): OneAiPanelGeometry {
  if (typeof window === "undefined") return g;
  const maxW = Math.max(ONE_AI_PANEL_MIN_W, window.innerWidth - 16);
  const maxH = Math.max(ONE_AI_PANEL_MIN_H, window.innerHeight - 16);
  const width = Math.min(maxW, Math.max(ONE_AI_PANEL_MIN_W, Math.round(g.width)));
  const height = Math.min(maxH, Math.max(ONE_AI_PANEL_MIN_H, Math.round(g.height)));
  const left = Math.min(window.innerWidth - width - 8, Math.max(8, Math.round(g.left)));
  const top = Math.min(window.innerHeight - height - 8, Math.max(8, Math.round(g.top)));
  return { left, top, width, height };
}

export function loadOneAiPanelGeometry(): OneAiPanelGeometry {
  try {
    const raw = window.localStorage.getItem(ONE_AI_PANEL_STORAGE_KEY);
    if (!raw) return defaultOneAiPanelGeometry();
    const parsed = JSON.parse(raw) as Partial<OneAiPanelGeometry>;
    if (
      typeof parsed.left !== "number" ||
      typeof parsed.top !== "number" ||
      typeof parsed.width !== "number" ||
      typeof parsed.height !== "number"
    ) {
      return defaultOneAiPanelGeometry();
    }
    return clampOneAiPanelGeometry({
      left: parsed.left,
      top: parsed.top,
      width: parsed.width,
      height: parsed.height,
    });
  } catch {
    return defaultOneAiPanelGeometry();
  }
}

export function saveOneAiPanelGeometry(g: OneAiPanelGeometry): void {
  try {
    window.localStorage.setItem(ONE_AI_PANEL_STORAGE_KEY, JSON.stringify(clampOneAiPanelGeometry(g)));
  } catch {
    /* ignore */
  }
}
